import { motion } from 'framer-motion'
import type { PaymentMethod } from './types'
import { PAYMENT_METHODS } from './types'
import { Box, Typography, Card } from '@mui/material'

interface PaymentCardProps {
  paymentMethod: PaymentMethod | { id: string; name: string }
  cardNumber?: string
  cardHolder?: string
}

// Helper function to darken a hex color
const darkenColor = (hex: string, percent: number): string => {
  const num = parseInt(hex.replace('#', ''), 16)
  const r = Math.max(0, Math.floor((num >> 16) * (1 - percent)))
  const g = Math.max(0, Math.floor(((num >> 8) & 0x00ff) * (1 - percent)))
  const b = Math.max(0, Math.floor((num & 0x0000ff) * (1 - percent)))
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}

export default function PaymentCard({
  paymentMethod,
  cardNumber = '**** **** **** ****',
  cardHolder = 'CARD HOLDER',
}: PaymentCardProps) {
  // Get the full payment method data to access color (yawada)
  const fullPaymentMethod = PAYMENT_METHODS.find((pm) => pm.id === paymentMethod.id)
  const baseColor = fullPaymentMethod?.color || '#6b7280'
  const darkerColor = darkenColor(baseColor, 0.2)


  const getCardStyle = () => {
    
    switch (paymentMethod.id) {
      
      case 'ecocash':
        // Blue and white
        return {
          gradientColors: ['#2563eb', '#1e40af'],
          logoColor: 'text-white',
          textColor: 'text-white',
        }
      case 'onemoney':
        // Orange and black
        return {
          gradientColors: ['#f97316', '#ea580c'],
          logoColor: 'text-black',
          textColor: 'text-black',
        }
      case 'omari':
        // Greenish
        return {
          gradientColors: ['#22c55e', '#16a34a'], 
          logo: "O'Mari",
          logoColor: 'text-white',
          textColor: 'text-white',
        }
      case 'innbucks':
        // Dark blue, white, and green zvese ipapo
        return {
          gradientColors: ['#1e40af', '#1e3a8a'], 
          logo: 'InnBucks',
          logoColor: 'text-white',
          textColor: 'text-white',
          accentColor: '#22c55e', 
        }
      case 'visa':
        return {
          gradientColors: ['#4f46e5', '#4338ca'],
          logo: 'VISA',
          logoColor: 'text-white',
          textColor: 'text-white',
        }
      case 'mastercard':
        return {
          gradientColors: ['#ef4444', '#dc2626'],
          logo: 'Mastercard',
          logoColor: 'text-white',
          textColor: 'text-white',
        }
      case 'zimswitch':
        return {
          gradientColors: ['#d97706', '#b45309'],
          logo: 'ZimSwitch',
          logoColor: 'text-white',
          textColor: 'text-white',
        }
      default:
        return {
          gradientColors: ['#6b7280', '#4b5563'],
          logo: 'Payment Card',
          logoColor: 'text-white',
          textColor: 'text-white',
        }
    }
  }

  const style = getCardStyle()
  
  // Create gradient style with brand-specific colors
  const gradientStyle = {
    background: `linear-gradient(135deg, ${style.gradientColors[0]}, ${style.gradientColors[1]},  ${baseColor}, ${darkerColor})`,
  }

  return (
    <motion.div
      key={paymentMethod.id}
      initial={{ opacity: 0, rotateY: -90 }}
      animate={{ opacity: 1, rotateY: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      whileHover={{ scale: 1.02 }}
      style={{ position: 'relative' }}
    >
      <Card
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 384,
          height: 224,
          borderRadius: 4,
          boxShadow: 12,
          overflow: 'hidden',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          ...gradientStyle,
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0, opacity: 0.2 }}>
          <Box sx={{ position: 'absolute', top: 0, right: 0, width: 128, height: 128, bgcolor: 'background.paper', borderRadius: '50%', mr: -16, mt: -16 }} />
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: 96, height: 96, bgcolor: 'background.paper', borderRadius: '50%', ml: -12, mb: -12 }} />
          {paymentMethod.id === 'innbucks' && style.accentColor && (
            <Box sx={{ position: 'absolute', top: '50%', right: 0, width: 80, height: 80, bgcolor: style.accentColor || '#22c55e', borderRadius: '50%', opacity: 0.3, mr: -10 }} />
          )}
        </Box>

        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ width: 48, height: 32, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 1, backdropFilter: 'blur(4px)', mb: 1 }} />
              <Typography variant="caption" sx={{ color: style.textColor, opacity: 0.8 }}>
                {paymentMethod.name}
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: style.logoColor }}>
              {style.logo}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontFamily: 'monospace', letterSpacing: 2, fontSize: 20, color: style.textColor }}>
              {cardNumber}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="caption" sx={{ color: style.textColor, opacity: 0.8, mb: 0.5 }}>
                CARD HOLDER
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: style.textColor, textTransform: 'uppercase' }}>
                {cardHolder}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <Typography variant="caption" sx={{ color: style.textColor, opacity: 0.8, mb: 0.5 }}>
                VALID THRU
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: style.textColor }}>
                **/**
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ position: 'absolute', top: 24, left: 24, width: 40, height: 32, borderRadius: 1, boxShadow: 4, background: 'linear-gradient(135deg, #f59e0b, #92400e)' }} />
      </Card>
    </motion.div>
  )
}

